import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import * as _ from 'lodash';

import { useStateWithGetter } from '@bld/bld-libs/utils';

import FieldBulletinBoardsService from './FieldBulletinBoardsService';
import FieldBulletinBoardsApi from './FieldBulletinBoardsApi';

import SessionStorageService from '../utilities/SessionStorageService';
import _masterSlideLoader from '../../utilities/masterSlideLoader';
import { messageUtility } from '../../utilities/messageUtility';
import { isLoginUserPartnerCompany } from '../../utilities/roleUtility';
import { useFieldBulletinBoardUnreadCount } from '../fieldBulletinBoard/FieldBulletinBoardsUnreadCountContext.js';

import { STATUS_CODE, BACK_EVENT_SOURCE } from '../../utilities/constants';

const FieldBulletinBoardDetailCtx = createContext();

function FieldBulletinBoardDetailProvider({
  children,
  fieldBulletinBoardId,
  fieldBulletinBoardSearchParam = {},
}) {
  const [detailInfo, setDetailInfo, getDetailInfo] = useStateWithGetter({});
  const [, setModalDisplayInfo, getModalDisplayInfo] = useStateWithGetter({});
  const [, setButtonDisplayInfo, getButtonDisplayInfo] = useStateWithGetter({
    edit: false,
    stop: false,
  });
  const [, setErrorDialogProperty, getErrorDialogProperty] = useStateWithGetter(
    {}
  );
  const [, setLoadingFlg, getLoadingFlg] = useStateWithGetter(false);

  const fieldIdRef = useRef(SessionStorageService.getFieldId());
  // 調整会議・入退場の機能一覧エリアに表示させる現場掲示板未読件数マークを最新化する。
  const { fetchUnreadCount } = useFieldBulletinBoardUnreadCount();
  const [shouldRegisterAlreadyRead, setShouldRegisterAlreadyRead ]= useState(false); 

  /**
   * 初期表示
   */
  useEffect(() => {
    FieldBulletinBoardsService.loadFieldInfo()
      .then(response => {
        if (_.isEmpty(response) || !response.isAccessible) {
          return window.location.replace('/error-permission');
        }

        fetchDetail();
      })
      .catch(error => {
        return window.location.replace('/error-permission');
      });
  }, []);

  // 詳細情報表示後に既読処理 / 未読件数更新処理を実行する。
  useEffect(() => {
    if (shouldRegisterAlreadyRead) {
      if (isLoginUserPartnerCompany()
        && !SessionStorageService.isProxyLogin()
      ) {

        // 既読登録API呼び出し処理は共通のエラーハンドリング以外は対応しない
        FieldBulletinBoardsApi.registerAlreadyRead(
          fieldIdRef.current,
          fieldBulletinBoardId
        ).then(() => {
          fetchUnreadCount();
        });
      }
    }
  }, [shouldRegisterAlreadyRead]);

  const fetchDetail = () => {
    FieldBulletinBoardsApi.fetchFieldBulletinBoard(
      fieldIdRef.current,
      fieldBulletinBoardId
    ).then(response => {
      if (!response.result) {
        return window.location.replace('/error-500');
      }

      switch (response.status) {
        case STATUS_CODE.badRequest:
        case STATUS_CODE.notFound:
          return window.location.replace('/error-permission');
        default:
          break;
      }

      setDetailInfo(response.data.entities.field_bulletin_board);
      setButtonDisplayInfo(prev => {
        return {
          ...prev,
          ...{
            edit: response.data.can_display_edit_button,
            stop: response.data.can_display_stop_button,
          },
        };
      });
      setShouldRegisterAlreadyRead(!response.data.entities.field_bulletin_board.already_read);
    });
  };

  /**
   * パンくず押下時
   * */
  const handleBack = backEventSource => {
    if (backEventSource === BACK_EVENT_SOURCE.MASTER_PAGE_HEADER) {
      // 現場掲示板を閉じてサービストップに戻る処理
      _masterSlideLoader.close();
    } else {
      _masterSlideLoader.show('FieldBulletinBoards', {
        state: {
          id: fieldBulletinBoardId,
          fieldBulletinBoardSearchParam,
        },
      });
    }
  };

  /**
   * 編集ボタン押下時
   */
  const handleEdit = () => {
    _masterSlideLoader.show('FieldBulletinBoardEdit', {
      state: {
        id: fieldBulletinBoardId,
        fieldBulletinBoardSearchParam,
      },
    });
  };

  /**
   * 停止ボタン押下時
   */
  const handleStop = () => {
    // 公開停止確認ダイアログ表示
    setModalDisplayInfo({ stop: true });
  };

  /**
   * 公開停止を実行
   */
  const executeStop = async () => {
    // 公開停止APIを実行する
    if (getLoadingFlg()) {
      return;
    }

    setLoadingFlg(true);
    await FieldBulletinBoardsApi.stopFieldBulletinBoard(
      fieldIdRef.current,
      fieldBulletinBoardId,
      { lock_version: detailInfo.lock_version }
    )
      .then(response => {
        closeConfirmDialog();
        if (!response) {
          return;
        }

        if (response.result) {
          setModalDisplayInfo({ stopComplete: true });
          fetchDetail();
          return;
        }

        if (response.status === STATUS_CODE.badRequest) {
          setErrorDialogProperty({
            title: messageUtility.getMessage('E-000-166-03'),
            message: `${messageUtility.getMessage('E-000-166-02')}
              ${response.data.message}`,
          });

          setModalDisplayInfo({ error: true });
          return;
        }

        if (
          response.status == STATUS_CODE.forbidden ||
          response.status == STATUS_CODE.notfound
        ) {
          return window.location.replace('/error-permission');
        }

        if (response.status == STATUS_CODE.conflict) {
          setErrorDialogProperty({
            title: messageUtility.getMessage('E-000-166-03'),
            message: `${messageUtility.getMessage('E-000-166-04')}`,
          });

          setModalDisplayInfo({ error: true });
          return;
        }

        // 通常の画面操作以外でエラーが発生した場合
        return window.location.replace('/error-500');
      })
      .finally(() => {
        setLoadingFlg(false);
      });
  };

  /**
   * 確認系ダイアログを閉じる
   * */
  const closeConfirmDialog = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });
  };

  /**
   * 完了系ダイアログを閉じる
   * */
  const closeCompleteDialog = () => {
    setModalDisplayInfo({ [_.findKey(getModalDisplayInfo())]: false });
  };

  /**
   * ファイルダウンロードを実行する
   * @param {number} 添付ファイルID
   */
  const handleDownload = async documentId => {
    await FieldBulletinBoardsApi.downloadFile(
      fieldIdRef.current,
      fieldBulletinBoardId,
      documentId
    ).then(response => {
      if (!response) {
        return;
      }

      if (response.result) {
        const name = response.data.entities.file_name;
        const path = response.data.entities.presigned_url;

        fetch(path).then(response => {
          response.blob().then(blob => {
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', name);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          });
        });

        return;
      }

      if (response.data.status == STATUS_CODE.forbidden) {
        setModalDisplayInfo({ error: true });
        setErrorDialogProperty({
          title: messageUtility.getMessage('E-001-402'),
          message: messageUtility.getMessage('E-000-166-05'),
        });
        return;
      }

      return window.location.replace('/error-500');
    });
  };

  const contextValue = {
    getDetailInfo,
    handleDownload,
    handleBack,
    handleStop,
    handleEdit,
    executeStop,
    getButtonDisplayInfo,
    getModalDisplayInfo,
    getErrorDialogProperty,
    closeConfirmDialog,
    closeCompleteDialog,
  };

  return (
    <FieldBulletinBoardDetailCtx.Provider value={contextValue}>
      {children}
    </FieldBulletinBoardDetailCtx.Provider>
  );
}

const useFieldBulletinBoardDetail = () => {
  const context = useContext(FieldBulletinBoardDetailCtx);
  return context;
};

export { useFieldBulletinBoardDetail, FieldBulletinBoardDetailProvider };



// WEBPACK FOOTER //
// ./src/services/fieldBulletinBoard/FieldBulletinBoardDetailContext.js